import dayjs, { type Dayjs } from 'dayjs';
import type { SubtaskDraft, TaskDraft, WorkstreamDraft } from '../../features/aiPlanner/plannerApi';

/* UI-only annotations on top of the draft. `_cid` and `_dropped` are stripped
 * before anything is sent; `_byUser` is kept so the AI respects hand edits. */
export type SubUI = SubtaskDraft & { _cid: string; _dropped?: boolean };
export type TaskUI = Omit<TaskDraft, 'subtasks'> & { _cid: string; _dropped?: boolean; subtasks: SubUI[] };
export type WsUI = Omit<WorkstreamDraft, 'tasks'> & { tasks: TaskUI[] };

let seq = 0;
export const cid = () => `c${Date.now().toString(36)}${(seq++).toString(36)}`;

export const PRIORITY_OPTS = ['Critical', 'High', 'Medium', 'Low'].map((p) => ({ value: p, label: p }));
export const SIZE_LABEL: Record<string, string> = { small: 'small', medium: 'medium-sized', large: 'large' };

export function annotate(workstreams: WorkstreamDraft[] | undefined | null): WsUI[] {
  return (workstreams ?? []).map((ws) => ({
    ...ws,
    tasks: (ws.tasks ?? []).map((t) => ({
      ...t, _cid: cid(),
      subtasks: (t.subtasks ?? []).map((s) => ({ ...s, _cid: cid() })),
    })),
  }));
}

/** Re-annotate a new version of the plan, carrying `_byUser` flags over by ref. */
export function reannotate(workstreams: WorkstreamDraft[] | undefined | null, prev: WsUI[]): WsUI[] {
  const flags = new Map<string, boolean>();
  prev.forEach((w) => w.tasks.forEach((t) => { if (t.ref && t._byUser) flags.set(t.ref, true); }));
  return annotate(workstreams).map((w) => ({
    ...w, tasks: w.tasks.map((t) => (t.ref && flags.get(t.ref) ? { ...t, _byUser: true } : t)),
  }));
}

function stripSub(s: SubUI): SubtaskDraft {
  const { _cid, _dropped, ...rest } = s;
  void _cid; void _dropped;
  return rest;
}
function stripTask(t: TaskUI): TaskDraft {
  const { _cid, _dropped, subtasks, ...rest } = t;
  void _dropped;
  return {
    ...rest,
    ref: rest.ref || `user.${_cid}`,
    subtasks: subtasks.filter((s) => !s._dropped && s.title.trim()).map(stripSub),
  };
}
/** Remove UI-only fields and dropped items. */
export function strip(ws: WsUI[]): WorkstreamDraft[] {
  return ws
    .map((w) => ({
      key: w.key, title: w.title,
      tasks: w.tasks.filter((t) => !t._dropped && t.title.trim()).map(stripTask),
    }))
    .filter((w) => w.tasks.length > 0);
}

export function countItems(ws: WsUI[]): number {
  return ws.reduce((n, w) => n + w.tasks.filter((t) => !t._dropped && t.title.trim())
    .reduce((m, t) => m + 1 + t.subtasks.filter((s) => !s._dropped && s.title.trim()).length, 0), 0);
}

export const newTask = (): TaskUI => ({
  _cid: cid(), _byUser: true, ref: null, title: '', description: '', priority: 'Medium',
  due_offset_days: null, due_date: null, suggested_assignee_id: null, assignee_role_hint: null,
  assignee_reason: null, depends_on: [], needs_confirmation: false, subtasks: [],
});
export const newSub = (): SubUI => ({
  _cid: cid(), _byUser: true, title: '', description: '', due_offset_days: null, due_date: null,
  suggested_assignee_id: null, assignee_role_hint: null, assignee_reason: null,
});

type Dated = { due_date?: string | null; due_offset_days?: number | null };

/** A date the user set wins; otherwise event date + offset. */
export function dueValue(item: Dated, eventDate?: string | null): Dayjs | null {
  if (item.due_date) return dayjs(item.due_date);
  if (eventDate && item.due_offset_days != null) return dayjs(eventDate).add(item.due_offset_days, 'day');
  return null;
}
export function dueHint(item: Dated): string {
  if (item.due_date) return 'set by you';
  if (item.due_offset_days == null) return 'no date yet';
  const d = item.due_offset_days;
  return d === 0 ? 'on the day' : d < 0 ? `${-d} day(s) before the event` : `${d} day(s) after the event`;
}
export const prettyDate = (d: string) => dayjs(d).format('D MMMM YYYY');
